import * as React from 'react';
import {
  applyServerErrors,
  BottomSheet,
  BottomSheetContent,
  Button,
  Form,
  FormField,
  newImpoundSchema,
  OptionSheet,
  toast,
  useZodForm,
  type NewImpoundValues,
} from '@relay-towing/ui';
import { useNavigate } from 'react-router-dom';
import { useCreateImpound } from '../../hooks/useCreateImpound';
import { useLocations } from '../../hooks/useLocations';
import type { CallRecord } from '../calls/callRecord';
import { CallPickerSheet, callChoiceLabel } from '../shell/CallPickerSheet';

/**
 * Bring a vehicle into the lot from the phone's Impounds, for an office
 * member with "Create impounds": the dashboard's New Impound, on its
 * `newImpoundSchema` and `POST /impounds`. An impound is always a call's,
 * so it asks which call and which lot; the vehicle is the call's own. The
 * server refuses a call that already has one, and its words are shown.
 * No Figma frame exists for it; it is laid out as the phone's other forms.
 */
export function NewImpoundSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const formId = React.useId();
  const navigate = useNavigate();
  const create = useCreateImpound();
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent
        title="New impound"
        footer={
          <>
            <Button
              variant="secondary"
              size="xl"
              pill
              disabled={create.isPending}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={formId}
              size="xl"
              pill
              loading={create.isPending}
            >
              Create
            </Button>
          </>
        }
      >
        <NewImpoundForm
          id={formId}
          onCreate={async (values) => {
            const impound = await create.mutateAsync({
              dispatchId: values.dispatchId,
              locationId: values.locationId,
              storageLot: values.storageLot,
            });
            onOpenChange(false);
            toast.success(`Impound ${impound.impoundNumber} created`);
            navigate(`/impounds/${impound.id}`);
          }}
        />
      </BottomSheetContent>
    </BottomSheet>
  );
}

/** The fields, made fresh each time the sheet opens. */
function NewImpoundForm({
  id,
  onCreate,
}: {
  id: string;
  onCreate: (values: NewImpoundValues) => Promise<void>;
}) {
  const { data: locations } = useLocations();
  const [picking, setPicking] = React.useState(false);
  const [call, setCall] = React.useState<CallRecord | null>(null);
  const form = useZodForm(newImpoundSchema, {
    defaultValues: { dispatchId: '', locationId: '', storageLot: '' },
  });
  const create = async (values: NewImpoundValues) => {
    try {
      await onCreate(values);
    } catch (error) {
      // The server's own words: the call already has an impound.
      applyServerErrors(form, error);
    }
  };
  return (
    <Form form={form} id={id} gap={4} onSubmit={create}>
      <FormField form={form} name="dispatchId" label="Call">
        {() => (
          <Button
            variant="outline"
            size="xl"
            fullWidth
            onClick={() => setPicking(true)}
          >
            {call ? callChoiceLabel(call) : 'Choose a call'}
          </Button>
        )}
      </FormField>
      <CallPickerSheet
        open={picking}
        onOpenChange={setPicking}
        onPick={(picked) => {
          setCall(picked);
          form.setValue('dispatchId', picked.id, { shouldValidate: true });
        }}
      />
      <FormField form={form} name="locationId" label="Storage lot">
        {(field) => (
          <OptionSheet
            title="Storage lot"
            placeholder="Choose a lot"
            options={(locations ?? []).map((location) => ({
              value: location.id,
              label: location.name,
            }))}
            value={field.value || undefined}
            onValueChange={(locationId) => {
              field.onChange(locationId);
              // The server still wants the lot's name as storageLot.
              form.setValue(
                'storageLot',
                locations?.find((location) => location.id === locationId)
                  ?.name ?? ''
              );
            }}
          />
        )}
      </FormField>
    </Form>
  );
}
